import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'

import { ErrorBox } from '../components/ui'
import { loginUrl, onTermsPending, useApi } from './api'
import { useI18n } from './i18n'
import { sessionEnded, watchSession } from './sessionEnd'
import type { Me } from './types'

interface Session {
  me: Me
  reload: () => void
}

const SessionContext = createContext<Session | null>(null)

const LOGIN_RETRIED = 'pedfx.loginRetried'

type FlagStore = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>

function tabStore(): FlagStore | null {
  try {
    return window.sessionStorage
  } catch {
    return null
  }
}

/**
 * Whether a failed login is started once more instead of shown. The platform's login state can be gone
 * while Keycloak's session holds: tabs restored together each start a login and overwrite each other's
 * state, a login page stays open past ten minutes, or an old address still carries the error. The second
 * round then passes without a password. Once per tab until a login succeeds, so it never loops.
 */
export function retryFailedLogin(authError: string | null, store: FlagStore | null): boolean {
  if (authError !== 'login_failed' || store === null) return false
  try {
    if (store.getItem(LOGIN_RETRIED)) return false
    store.setItem(LOGIN_RETRIED, '1')
    return true
  } catch {
    return false
  }
}

/** Renders its children only for a logged-in user; otherwise starts the login (AUTH-01). */
export function SessionGate({ children }: { children: ReactNode }) {
  const { t } = useI18n()
  const { data, error, reload } = useApi<Me>('/me')
  const authError = new URLSearchParams(window.location.search).get('auth_error')
  const needsLogin = error?.status === 401
  const [retrying] = useState(() => retryFailedLogin(authError, tabStore()))
  const signedIn = data !== undefined

  useEffect(() => (signedIn ? watchSession() : undefined), [signedIn])
  // Terms of use changed meanwhile: reading who the user is again shows them (AUTH-10).
  useEffect(() => onTermsPending(reload), [reload])

  useEffect(() => {
    // A session that ended while signed in is on its way to a login that asks for the password already.
    if (needsLogin && (!authError || retrying) && !sessionEnded()) window.location.assign(loginUrl(authError ? '/' : undefined))
  }, [needsLogin, authError, retrying])

  useEffect(() => {
    if (data) {
      try {
        tabStore()?.removeItem(LOGIN_RETRIED)
      } catch {
        // storage blocked: nothing was kept either
      }
    }
  }, [data])

  if (data) return <SessionContext.Provider value={{ me: data, reload }}>{children}</SessionContext.Provider>
  if (needsLogin && authError && !retrying) {
    return (
      <main className="container narrow">
        <p className="alert">{authError === 'account_inactive' ? t('accountInactive') : t('loginFailed')}</p>
        <a className="button" href={loginUrl('/')}>
          {t('loginAgain')}
        </a>
      </main>
    )
  }
  if (error && !needsLogin) return <ErrorBox error={error} />
  return <p className="muted center">{t('loading')}</p>
}

export function useSession(): Session {
  const session = useContext(SessionContext)
  if (session === null) throw new Error('useSession must be used inside SessionGate')
  return session
}
